import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
// Session-free client: a stale login token in localStorage must never
// poison public reads (the careers/survey "empty page" bug class).
import { publicSupabase as supabase } from '../lib/supabase';
import { Button, Field } from '../components/ui';
import gsap from 'gsap';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[+]?[\d\s()-]{7,}$/;

// Animated Info Card Component
function AnimatedInfoCard({ icon, title, content, index }) {
  const cardRef = useRef(null);

  useEffect(() => {
    if (!cardRef.current) return;

    gsap.set(cardRef.current, { opacity: 0, x: 30 });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(cardRef.current, {
              opacity: 1,
              x: 0,
              duration: 0.6,
              delay: index * 0.15,
              ease: 'power3.out',
            });
            observer.unobserve(cardRef.current);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div ref={cardRef} className="flex items-start gap-4">
      <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-gray-900">{title}</h3>
        {content}
      </div>
    </div>
  );
}

export default function Contact() {
  const { t } = useTranslation();
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const headerRef = useRef(null);
  const formRef = useRef(null);
  const statusRef = useRef(null);

  // Animate header on mount
  useEffect(() => {
    if (headerRef.current) {
      gsap.fromTo(
        headerRef.current.children,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out' }
      );
    }
  }, []);

  // Animate form on mount
  useEffect(() => {
    if (formRef.current) {
      gsap.fromTo(
        formRef.current,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, delay: 0.3, ease: 'power3.out' }
      );
    }
  }, []);

  const onSubmit = async (data) => {
    setSubmitting(true);
    setSubmitStatus(null);

    try {
      const { error } = await supabase
        .from('contact_submissions')
        .insert({
          name: data.name,
          email: data.email,
          phone: data.phone || null,
          subject: data.subject || null,
          message: data.message,
          status: 'new',
        });

      if (error) throw error;

      setSubmitStatus('success');
      reset();
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
      // Bring the result banner into view — on mobile it can land off-screen.
      requestAnimationFrame(() => {
        statusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  };

  return (
    <div className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div ref={headerRef} className="text-center mb-12">
          <h1 className="section-title">{t('contact.title')}</h1>
          <p className="section-subtitle">{t('contact.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div ref={formRef} className="card p-6 md:p-8">
            <div ref={statusRef}>
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-4 mb-6" role="status">
                  {t('contact.form.success')}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6" role="alert">
                  {t('contact.form.error')}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
              <Field id="contact-name" label={t('contact.form.name')} required error={errors.name?.message}>
                <input
                  id="contact-name"
                  type="text"
                  aria-invalid={!!errors.name}
                  {...register('name', { required: t('contact.form.nameRequired', 'Please enter your name') })}
                  className="input-field"
                />
              </Field>

              <Field id="contact-email" label={t('contact.form.email')} required error={errors.email?.message}>
                <input
                  id="contact-email"
                  type="email"
                  aria-invalid={!!errors.email}
                  {...register('email', {
                    required: t('contact.form.emailRequired', 'Please enter your email'),
                    pattern: { value: EMAIL_PATTERN, message: t('contact.form.emailInvalid', 'Enter a valid email address') },
                  })}
                  className="input-field"
                />
              </Field>

              <Field id="contact-phone" label={t('contact.form.phone')} error={errors.phone?.message}>
                <input
                  id="contact-phone"
                  type="tel"
                  aria-invalid={!!errors.phone}
                  {...register('phone', {
                    pattern: { value: PHONE_PATTERN, message: t('contact.form.phoneInvalid', 'Enter a valid phone number') },
                  })}
                  className="input-field"
                />
              </Field>

              <Field id="contact-subject" label={t('contact.form.subject')}>
                <input
                  id="contact-subject"
                  type="text"
                  {...register('subject')}
                  className="input-field"
                />
              </Field>

              <Field id="contact-message" label={t('contact.form.message')} required error={errors.message?.message}>
                <textarea
                  id="contact-message"
                  rows={5}
                  aria-invalid={!!errors.message}
                  {...register('message', { required: t('contact.form.messageRequired', 'Please enter a message') })}
                  className="input-field"
                />
              </Field>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full disabled:opacity-50"
              >
                {submitting ? t('contact.form.sending') : t('contact.form.send')}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div>
            <h2 className="text-2xl font-semibold mb-6">{t('contact.info.title')}</h2>

            <div className="space-y-6">
              <AnimatedInfoCard
                index={0}
                icon={
                  <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                }
                title={t('contact.info.email')}
                content={<p className="text-gray-600">ampera.dalemkaum@gmail.com</p>}
              />

              <AnimatedInfoCard
                index={1}
                icon={
                  <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                }
                title={t('contact.info.phone')}
                content={<p className="text-gray-600">+62 819-5000-165</p>}
              />

              <AnimatedInfoCard
                index={2}
                icon={
                  <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                }
                title={t('contact.info.address')}
                content={
                  <p className="text-gray-600">
                    Jl. Khp Hasan Mustopa No.31,<br />
                    Neglasari, Kec. Cibeunying Kaler,<br />
                    Bandung, Jawa Barat 40124, Indonesia
                  </p>
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
